import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './DeviceRegistration.css';
import './Dashboard.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function DeviceRegistration({ onBack, embedded = false, isTopLevel = false }) {
  const { t } = useTranslation();
  
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningDevice, setAssigningDevice] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsDevice, setSettingsDevice] = useState(null);
  const [settingsDeviceName, setSettingsDeviceName] = useState('');
  const [settingsOrientation, setSettingsOrientation] = useState('landscape');
  const [settingsSubscriptionId, setSettingsSubscriptionId] = useState('');
  const [settingsModalError, setSettingsModalError] = useState('');
  const [subscriptionDropdownOpen, setSubscriptionDropdownOpen] = useState(false);
  const subscriptionDropdownRef = useRef(null);

  useEffect(() => {
    if (!subscriptionDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (subscriptionDropdownRef.current && !subscriptionDropdownRef.current.contains(e.target)) {
        setSubscriptionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [subscriptionDropdownOpen]);

  const fetchDevicesSnapshot = useCallback(async () => {
    const response = await fetch(`${API_BASE}/api/devices/my-devices`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch devices');
    }

    const data = await response.json();
    return data.devices || [];
  }, []);

  // Fetch devices pre-registered for current user (by email from session)
  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const latestDevices = await fetchDevicesSnapshot();
      setDevices(latestDevices);
      setError('');
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      setError(t('deviceRegistration.fetchError') || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [fetchDevicesSnapshot, t]);

  useEffect(() => {
    fetchDevices();
    fetchSubscriptions();
    fetchSchedules();
  }, [fetchDevices]);

  // Fetch user's subscriptions
  const fetchSubscriptions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/subscription/list`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch subscriptions');
      const data = await response.json();
      setSubscriptions(data.subscriptions || []);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
    }
  };

  // Fetch available schedules
  const fetchSchedules = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/schedule/available`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch schedules');
      const data = await response.json();
      setSchedules(data.schedules || []);
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    }
  };

  // Canonical size code <-> resolution mapping (matches gallery-pipeline's resolutionToDeviceSize
  // and backend/routes/adminRoutes.js's SIZE_CODE_RESOLUTIONS)
  const DEVICE_SIZE_RESOLUTIONS = {
    '13': ['1200x1600', '1600x1200'],
    '25': ['1800x3200', '3200x1800'],
    '28': ['2160x3060', '3060x2160'],
    '31': ['1440x2560', '2560x1440'],
    '40': ['3060x4320', '4320x3060']
  };
  const DEVICE_SIZE_LABELS = { '13': '13.3 inch', '25': '25.3 inch', '28': '28.5 inch', '31': '31.5 inch', '40': '40.5 inch' };

  const getNormalizedDeviceSizeCode = (device) => {
    const rawResolution = String(device?.resolution || '').trim().toLowerCase();
    const rawName = String(device?.deviceName || '').trim().toLowerCase();
    const rawProductId = String(device?.productId || '').trim().toUpperCase();

    const productSizeCode = rawProductId.replace(/^NETRONIX-/, '');
    if (DEVICE_SIZE_LABELS[productSizeCode]) {
      return productSizeCode;
    }

    for (const [code, resolutions] of Object.entries(DEVICE_SIZE_RESOLUTIONS)) {
      if (resolutions.includes(rawResolution) || rawName.includes(code)) {
        return code;
      }
    }

    return '';
  };

  const getDeviceSizeLabel = (device) => {
    const sizeCode = getNormalizedDeviceSizeCode(device);
    return DEVICE_SIZE_LABELS[sizeCode] ? `${DEVICE_SIZE_LABELS[sizeCode]} size` : '-';
  };

  const getEffectiveProductId = (device) => {
    const productId = String(device?.productId || '').trim().toUpperCase();
    const sizeCode = productId.replace(/^NETRONIX-/, '');
    if (DEVICE_SIZE_LABELS[sizeCode]) {
      return productId;
    }

    const detectedSizeCode = getNormalizedDeviceSizeCode(device);
    return detectedSizeCode ? `NETRONIX-${detectedSizeCode}` : '';
  };

  // Get orientation value (landscape or portrait)
  const getOrientation = (resolution) => {
    if (!resolution) return 'landscape';
    const [width, height] = resolution.split('x').map(Number);
    return width > height ? 'landscape' : 'portrait';
  };

  const getDeviceOrientation = (device) => {
    const explicit = String(device?.orientation || '').trim();
    if (explicit === 'landscape' || explicit === 'portrait') {
      return explicit;
    }
    return getOrientation(device?.resolution);
  };

  const isBrowserScheduleOption = (schedule) => {
    const category = String(schedule?.scheduleCategory || '').trim().toLowerCase();
    if (category === 'browser') {
      return true;
    }

    const planType = String(schedule?.planType || '').trim().toLowerCase();
    if (planType === 'browser') {
      return true;
    }

    const scheduleName = String(schedule?.name || '').trim().toLowerCase();
    if (scheduleName.includes('web')) {
      return true;
    }

    return (
      scheduleName.includes('web landscape') ||
      scheduleName.includes('web portrait') ||
      /(^|\s|\()web(\s|\)|$)/.test(scheduleName)
    );
  };

  const getPurchaseDateTimestamp = (value) => {
    const ts = Date.parse(value || '');
    return Number.isNaN(ts) ? 0 : ts;
  };

  const groupedSubscriptions = useMemo(() => {
    const sorted = [...subscriptions].sort((a, b) => {
      return getPurchaseDateTimestamp(b.purchaseDate) - getPurchaseDateTimestamp(a.purchaseDate);
    });

    const groups = new Map();
    sorted.forEach((sub) => {
      const groupName = String(sub.planName || 'Unknown Plan').trim() || 'Unknown Plan';
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName).push(sub);
    });

    return Array.from(groups.entries()).map(([name, items]) => ({
      name,
      items
    }));
  }, [subscriptions]);

  // Get schedules compatible with device (orientation + exact device size + subscription entitlement)
  const getCompatibleSchedules = (device) => {
    if (!device) return [];

    const deviceOrientation = getDeviceOrientation(device);
    const deviceType = getEffectiveProductId(device);
    const assignedSubscription = subscriptions.find(
      (sub) => sub.subscriptionId === device.subscriptionId
    );
    const allowedScheduleIds = new Set(assignedSubscription?.assignedScheduleIds || []);

    return schedules.filter(schedule => {
      if (isBrowserScheduleOption(schedule)) {
        return false;
      }

      // Match orientation
      if (schedule.orientation && schedule.orientation !== deviceOrientation) {
        return false;
      }

      // Reject unknown device types
      const deviceSizeCode = String(deviceType || '').replace(/^NETRONIX-/, '');
      if (!DEVICE_SIZE_LABELS[deviceSizeCode]) {
        return false;
      }

      // The backend computes slideshowDeviceMac{13,25,28,31,40} per schedule from the actual
      // resolution digits in its MusTrusT name (see scheduleCache.js's detectResolutionOrientSize),
      // which is more reliable than re-parsing the (possibly reformatted) displayName here.
      const mac = String(schedule[`slideshowDeviceMac${deviceSizeCode}`] || '').trim();
      if (!mac) {
        return false;
      }

      // Match subscription's allowed schedule IDs
      if (device.subscriptionId) {
        if (!allowedScheduleIds.has(schedule.id)) {
          return false;
        }
      }

      return true;
    });
  };

  // Group schedules by MusTrusT group name then orientation (same as browser dashboard)
  const groupSchedulesByGroup = (scheduleList) => {
    const groups = new Map();
    for (const s of scheduleList) {
      const key = s.groupName || '(グループなし)';
      if (!groups.has(key)) groups.set(key, { landscape: [], portrait: [] });
      const orient = String(s.orientation || '').toLowerCase() === 'portrait' ? 'portrait' : 'landscape';
      groups.get(key)[orient].push(s);
    }
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'ja'))
      .flatMap(([groupName, { landscape, portrait }]) => [
        ...(landscape.length ? [{ label: `${groupName}（横置き）`, schedules: landscape }] : []),
        ...(portrait.length ? [{ label: `${groupName}（縦置き）`, schedules: portrait }] : []),
      ]);
  };

  // Close assignment modal
  const handleCloseAssign = () => {
    setShowAssignModal(false);
    setAssigningDevice(null);
    setSelectedSubscription('');
    setSelectedSchedule('');
  };

  // Handle assignment submission
  const handleAssignSubmit = async () => {
    // Check if user selected "Unassign" option
    if (selectedSchedule === '__UNASSIGN__') {
      // Call unregister logic instead
      await handleUnregister(assigningDevice);
      handleCloseAssign();
      return;
    }

    if (!selectedSubscription || !selectedSchedule) {
      setError('サブスクリプションとスケジュールを選択してください');
      return;
    }

    // Call backend to perform assignment (claim + assign schedule + sync)
    try {
      const response = await fetch(`${API_BASE}/api/devices/assign-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          macAddress: assigningDevice.macAddress,
          scheduleId: selectedSchedule,
          subscriptionId: selectedSubscription
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || data.error || '割り当てに失敗しました');
      }

      // Close modal immediately
      handleCloseAssign();
      
      // Refresh device list to show updated assignment
      await fetchDevices();
      
      // Show success message
      setSuccess('デバイスをスケジュールに割り当てました');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to assign device:', err);
      setError(err.message || '割り当ての保存に失敗しました');
      setTimeout(() => setError(''), 5000);
    }
  };

  // Unregister device (move back to Default Group)
  const handleUnregister = async (device) => {
    try {
      const response = await fetch(`${API_BASE}/api/devices/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          macAddress: device.macAddress
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || '登録解除に失敗しました');
      }

      await fetchDevices();
      setSuccess(t('deviceRegistration.unregisterSuccess') || 'デバイスを既定グループに戻しました');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to unregister device:', err);
      setError(err.message || t('deviceRegistration.unregisterError') || '登録解除に失敗しました');
      setTimeout(() => setError(''), 5000);
    }
  };

  // Open device settings modal
  const handleOpenSettings = async (device) => {
    let latestDevice = device;

    try {
      const latestDevices = await fetchDevicesSnapshot();
      setDevices(latestDevices);
      const matched = latestDevices.find(d => d.macAddress === device.macAddress);
      if (matched) {
        latestDevice = matched;
      }
    } catch (err) {
      console.warn('Failed to refresh latest device before opening settings modal:', err);
    }


    setSettingsDevice(latestDevice);
    setSettingsDeviceName(latestDevice.deviceName || '');
    setSettingsOrientation(getDeviceOrientation(latestDevice));
    setSettingsSubscriptionId(latestDevice.subscriptionId || '');
    setShowSettingsModal(true);
  };

  // Close device settings modal
  const handleCloseSettings = () => {
    setShowSettingsModal(false);
    setSettingsDevice(null);
    setSettingsDeviceName('');
    setSettingsOrientation('landscape');
    setSettingsSubscriptionId('');
    setSettingsModalError('');
  };

  // Save device settings
  const handleSaveSettings = async () => {
    if (!settingsDevice) return;

    try {
      const orientationChanged = settingsOrientation !== getDeviceOrientation(settingsDevice);
      const subscriptionChanged = settingsSubscriptionId !== settingsDevice.subscriptionId;

      // Validate subscription uniqueness: check if another device is using the selected subscription
      if (settingsSubscriptionId && subscriptionChanged) {
        const otherDeviceWithSub = devices.find(d => 
          d.macAddress !== settingsDevice.macAddress && 
          d.subscriptionId === settingsSubscriptionId
        );
        if (otherDeviceWithSub) {
          throw new Error(`サブスクリプション ${settingsSubscriptionId} は既にデバイス "${otherDeviceWithSub.deviceName || otherDeviceWithSub.macAddress}" で使用されています`);
        }
      }

      // Step 1: If orientation/subscription changes, always clear assignment first.
      if (orientationChanged || subscriptionChanged) {
        const response = await fetch(`${API_BASE}/api/devices/unregister`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            macAddress: settingsDevice.macAddress
          })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to unregister device');
        }
      }

      // Step 2: Update device name if changed
      if (settingsDeviceName !== settingsDevice.deviceName) {
        const response = await fetch(`${API_BASE}/api/devices/${encodeURIComponent(settingsDevice.macAddress)}/name`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            deviceName: settingsDeviceName.trim()
          })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to update device name');
        }
      }

      // Step 3: Update orientation if changed
      if (orientationChanged) {
        const response = await fetch(`${API_BASE}/api/devices/${encodeURIComponent(settingsDevice.macAddress)}/orientation`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            orientation: settingsOrientation
          })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to update orientation');
        }
      }

      // Step 4: Update subscription in device cache (backend will handle persistence)
      if (subscriptionChanged) {
        const response = await fetch(`${API_BASE}/api/devices/${encodeURIComponent(settingsDevice.macAddress)}/subscription`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            subscriptionId: settingsSubscriptionId
          })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to update subscription');
        }
      }

      handleCloseSettings();
      await fetchDevices();
      setSuccess('デバイス設定を保存しました');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save device settings:', err);
      setSettingsModalError(err.message || 'デバイス設定の保存に失敗しました');
      setTimeout(() => setSettingsModalError(''), 5000);
    }
  };

  // Handle schedule change from dropdown on device card
  const handleScheduleChange = async (device, newScheduleId) => {
    if (!device.subscriptionId) {
      setError('スケジュールを選択するには、まずデバイス設定でサブスクリプション ID を割り当ててください');
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (newScheduleId === '') {
      await handleUnregister(device);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/devices/assign-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          macAddress: device.macAddress,
          scheduleId: newScheduleId,
          subscriptionId: device.subscriptionId
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || data.error || 'スケジュールの割り当てに失敗しました');
      }

      await fetchDevices();
      setSuccess('スケジュールを更新しました');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to change schedule:', err);
      setError(err.message || 'スケジュールの更新に失敗しました');
      setTimeout(() => setError(''), 5000);
    }
  };

  if (loading) {
    return (
      <div className="device-registration-container">
        <div className="device-loading">
          <div className="spinner"></div>
          <p>{t('deviceRegistration.loading') || 'Loading devices...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`device-registration-container${embedded || isTopLevel ? ' embedded' : ''}`}>
      {!embedded && !isTopLevel && onBack && (
        <div className="device-header">
          <div className="header-branding">
            <img src="/yyslogo192.png" alt="YY Solutions" className="yys-logo" />
            <div>
              <h1>CALMU GALLERY</h1>
            </div>
          </div>
          <button onClick={onBack} className="back-btn">
            ← {t('deviceRegistration.backToDashboard') || 'ダッシュボードに戻る'}
          </button>
        </div>
      )}

      <div className="device-content">
        {/* Page title removed per request: show only registered devices list */}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        <div className="devices-list">
          <h3>{t('dashboard.yourDevices') || 'あなたのデバイス'}</h3>
          
          {devices.length === 0 ? (
            <div className="no-devices">
              <p>{t('deviceRegistration.noDevices') || 'まだデバイスが登録されていません。'}</p>
            </div>
          ) : (
            <div className="devices-list devices-cards">
              {devices.map(device => (
                <div key={device.macAddress} className="device-card">
                  <div className="device-header">
                    {/* Column 1: Device Name */}
                    <div className="device-info">
                      <div className="device-name-row">
                        <div className="device-name-display">
                          <label className="device-property-label">{t('deviceRegistration.deviceLabel') || 'デバイス名'}</label>
                          <h4>{device.deviceName || device.macAddress}</h4>
                        </div>
                      </div>
                    </div>

                    {/* Column 2: Schedule */}
                    <div className="subscription-details-inline">
                      <div className="schedule-selector-group">
                        <label className="schedule-label">{t('dashboard.selectedSchedule') || '選択中のスケジュール:'}</label>
                        {device.subscriptionId ? (
                          <select
                            value={device.scheduleId || ''}
                            onChange={(e) => handleScheduleChange(device, e.target.value)}
                            className="schedule-select"
                          >
                            <option value="">-- スケジュールを選択 --</option>
                            {groupSchedulesByGroup(getCompatibleSchedules(device)).map(({ label, schedules: sg }) => (
                              <optgroup key={label} label={label}>
                                {sg.map(s => (
                                  <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        ) : (
                          <span className="device-property-value device-schedule-value">
                            サブスクリプションが未設定
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="device-actions">
                      <button
                        onClick={() => handleOpenSettings(device)}
                        className="action-btn"
                      >
                        ⚙️ {t('deviceRegistration.settings') || 'デバイス設定'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignModal && assigningDevice && (
        <div className="modal-overlay" onClick={handleCloseAssign}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>デバイスの割り当て - {assigningDevice.deviceName || assigningDevice.macAddress}</h3>
              <button onClick={handleCloseAssign} className="close-btn">✕</button>
            </div>
            <div className="modal-body">
              <div className="device-info" style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f0f4f8', borderRadius: '4px' }}>
                <p style={{ margin: '4px 0' }}>
                  <strong>デバイス:</strong> {assigningDevice?.deviceName || assigningDevice?.macAddress}
                </p>
                <p style={{ margin: '4px 0' }}>
                  <strong>サイズ:</strong> {getDeviceSizeLabel(assigningDevice)}
                </p>
                <p style={{ margin: '4px 0' }}>
                  <strong>向き:</strong> {getDeviceOrientation(assigningDevice) === 'landscape' ? '横向き' : '縦向き'}
                </p>
                <p style={{ margin: '4px 0' }}>
                  <strong>サブスクリプション:</strong> {selectedSubscription || '-'}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="schedule-select">スケジュール</label>
                {getCompatibleSchedules(assigningDevice).length === 0 ? (
                  <div style={{ 
                    padding: '12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404',
                    fontSize: '14px'
                  }}>
                    このデバイスの向きとサイズに対応したスケジュールがありません。
                    <br />
                    <small>別の向きを選択するか、スケジュール設定を確認してください。</small>
                  </div>
                ) : (
                  <select
                    id="schedule-select"
                    value={selectedSchedule}
                    onChange={(e) => setSelectedSchedule(e.target.value)}
                    className="form-select"
                  >
                    <option value="">-- スケジュールを選択 --</option>
                    <option value="__UNASSIGN__">登録解除</option>
                    {groupSchedulesByGroup(getCompatibleSchedules(assigningDevice)).map(({ label, schedules: sg }) => (
                      <optgroup key={label} label={label}>
                        {sg.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseAssign} className="cancel-button">
                {t('common.cancel') || 'キャンセル'}
              </button>
              <button onClick={handleAssignSubmit} className="ok-button">
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device Settings Modal */}
      {showSettingsModal && settingsDevice && (
        <div className="modal-overlay" onClick={handleCloseSettings}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>デバイス設定</h3>
              <button onClick={handleCloseSettings} className="close-btn">✕</button>
            </div>
            <div className="modal-body">
              {/* Error message display */}
              {settingsModalError && (
                <div className="error-message">
                  {settingsModalError}
                </div>
              )}
              
              {/* Device Name - Editable */}
              <div className="form-group">
                <label htmlFor="settings-device-name">デバイス名</label>
                <input
                  id="settings-device-name"
                  type="text"
                  value={settingsDeviceName}
                  onChange={(e) => setSettingsDeviceName(e.target.value)}
                  className="form-input"
                  placeholder="デバイス名を入力"
                />
              </div>

              {/* Subscription ID - Custom Dropdown */}
              <div className="form-group">
                <label>サブスクリプション ID</label>
                <div className="custom-sub-dropdown" ref={subscriptionDropdownRef}>
                  <button
                    type="button"
                    className="custom-sub-trigger"
                    onClick={() => setSubscriptionDropdownOpen(o => !o)}
                  >
                    <span className="custom-sub-trigger-text">
                      {settingsSubscriptionId || '-- 選択なし --'}
                    </span>
                    <span className="custom-sub-arrow">{subscriptionDropdownOpen ? '▲' : '▼'}</span>
                  </button>
                  {subscriptionDropdownOpen && (
                    <div className="custom-sub-list">
                      <div
                        className="custom-sub-option"
                        onClick={() => { setSettingsSubscriptionId(''); setSubscriptionDropdownOpen(false); }}
                      >
                        -- 選択なし --
                      </div>
                      {groupedSubscriptions.map(group => (
                        <div key={group.name}>
                          <div className="custom-sub-group-label">{group.name}</div>
                          {group.items.map(sub => (
                            <div
                              key={sub.subscriptionId}
                              className={`custom-sub-option${settingsSubscriptionId === sub.subscriptionId ? ' selected' : ''}`}
                              onClick={() => { setSettingsSubscriptionId(sub.subscriptionId); setSubscriptionDropdownOpen(false); }}
                            >
                              {sub.subscriptionId}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Orientation - Dropdown */}
              <div className="form-group">
                <label htmlFor="settings-orientation">向き</label>
                <select
                  id="settings-orientation"
                  value={settingsOrientation}
                  onChange={(e) => setSettingsOrientation(e.target.value)}
                  className="form-select"
                >
                  <option value="landscape">{t('deviceRegistration.landscape') || '横向き'}</option>
                  <option value="portrait">{t('deviceRegistration.portrait') || '縦向き'}</option>
                </select>
              </div>

              {/* Device Type - Text Display */}
              <div className="form-group">
                <label>デバイスタイプ</label>
                <div className="form-display">
                  {getDeviceSizeLabel(settingsDevice)}
                </div>
              </div>

              {/* MAC Address - Text Display */}
              <div className="form-group">
                <label>MAC アドレス</label>
                <div className="form-display">
                  {settingsDevice?.macAddress}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseSettings} className="cancel-button">
                {t('common.cancel') || 'キャンセル'}
              </button>
              <button onClick={handleSaveSettings} className="ok-button">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceRegistration;
